import classNames from 'classnames';
import { OpinionSymbolSelect } from 'components/Opinion/OpinionSymbolSelect';
import { Stock } from 'components/Stock/Stock.helper';
import { UserContext } from 'components/UserContext';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { useCallback, useContext, useMemo } from 'react';
import * as Yup from 'yup';
import { useOpinionMutation } from './OpinionFormik.hooks';

const MIN = 10;
const MAX = 1000;

type OpinionFormikProps = {
  stock?: Stock;
};

export const OpinionFormik = ({ stock }: OpinionFormikProps) => {
  const { user } = useContext(UserContext);
  const { mutate } = useOpinionMutation();
  const onSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      await mutate({ values });
      resetForm();
      setSubmitting(false);
    },
    [mutate]
  );

  const placeholder = useMemo(() => {
    if (!user) {
      return '로그인 후 작성이 가능합니다.';
    }

    if (!stock) {
      return `본인만의 투자 아이디어를 공유해 보세요. (최대 ${MAX}자)`;
    }

    return `${stock.krShortName}에 투자 아이디어를 공유해 보세요. (최대 ${MAX}자)`;
  }, [stock, user]);

  return (
    <Formik
      initialValues={{
        title: '',
        text: '',
        direction: '',
        symbol: stock?.symbol,
      }}
      validationSchema={Yup.object({
        title: Yup.string()
          .min(2, `최소 2자 이상 입력해 주세요.`)
          .max(30, `최대 30자 내로 입력해 주세요.`)
          .required('필수 입력 사항입니다.'),
        text: Yup.string()
          .min(MIN, `최소 ${MIN}자 이상 입력해 주세요.`)
          .max(MAX, `최대 ${MAX}자 내로 입력해 주세요.`)
          .required('필수 입력 사항입니다.'),
        direction: Yup.string().required('필수 입력 사항입니다.'),
      })}
      onSubmit={onSubmit}
    >
      {({ errors, touched, isSubmitting }) => {
        const disabled = !user || isSubmitting;
        return (
          <Form>
            <fieldset disabled={disabled}>
              <div className="d-flex justify-content-between align-items-center">
                {!stock && (
                  <OpinionSymbolSelect className="me-3" isDisabled={disabled} />
                )}
                <div role="group" aria-labelledby="my-radio-group">
                  <div className="form-check form-check-inline">
                    <Field
                      type="radio"
                      id="directionUp"
                      className={classNames('form-check-input', {
                        'is-invalid': touched.direction && errors.direction,
                      })}
                      name="direction"
                      value="up"
                    />
                    <label className="form-check-label" htmlFor="directionUp">
                      상승
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <Field
                      type="radio"
                      id="directionDown"
                      className={classNames('form-check-input', {
                        'is-invalid': touched.direction && errors.direction,
                      })}
                      name="direction"
                      value="down"
                    />
                    <label className="form-check-label" htmlFor="directionDown">
                      하락
                    </label>
                  </div>
                </div>
              </div>
              <Field
                name="title"
                className={classNames('form-control mt-3', {
                  'is-invalid': touched.title && errors.title,
                })}
                placeholder="제목"
              />
              <ErrorMessage
                component="div"
                className="invalid-feedback"
                name="title"
              />
              <Field
                as="textarea"
                name="text"
                className={classNames('form-control vh-50 mt-3', {
                  'is-invalid': touched.text && errors.text,
                })}
                rows="3"
                placeholder={placeholder}
              />
              <ErrorMessage
                component="div"
                className="invalid-feedback"
                name="text"
              />
              <div className="d-flex justify-content-end align-items-center">
                <button className="btn btn-light mt-3 px-5" type="submit">
                  등록
                </button>
              </div>
            </fieldset>
          </Form>
        );
      }}
    </Formik>
  );
};
